import { ComponentType, useEffect, useMemo, useState } from "react";
import {
  ArrowPathRoundedSquareIcon,
  BeakerIcon,
  BoltIcon,
  KeyIcon,
  PaintBrushIcon,
  RectangleStackIcon,
  ShieldCheckIcon,
  SparklesIcon,
  TruckIcon,
  UserCircleIcon,
  WrenchScrewdriverIcon,
} from "@heroicons/react/24/outline";

import useNotification from "../../components/shared/Notifications/useNotifications";
import { usePageNameContext } from "../../contexts/PageTitleContext";
import useCustomers from "../../data/Customer/useCustomers";
import useApplications from "../../data/Application/useApplications";
import useToolMutations from "../../data/Tools/useToolMutations";
import { IDefaultResponse } from "../../models/DefaultResponse";
import { IToolField } from "../../models/Tools/ToolModels";
import ToolActionModal from "../../components/Tools/ToolActionModal";
import ToolTile from "../../components/Tools/ToolTile";
import ToolValueResultModal from "../../components/Tools/ToolValueResultModal";

type ToolId =
  | "firebase-create-user"
  | "firebase-get-claims"
  | "firebase-update-claims"
  | "firebase-login"
  | "generate-secret"
  | "rgb-to-int"
  | "generate-signing-key"
  | "generate-application-token"
  | "generate-appsettings-app-token"
  | "generate-appsettings-appreg-token"
  | "generate-cache-refresh-token"
  | "generate-vehicle-lookup-token"
  | "gt-motive-parts-lookup"
  | "gt-motive-cache-export"
  | "import-ebay-images";

interface ToolDefinition {
  id: ToolId;
  title: string;
  subtitle: string;
  warningText: string;
  hoverClassName: string;
  icon: ComponentType<{ className?: string }>;
  fields: IToolField[];
  execute: (values: Record<string, unknown>) => Promise<IDefaultResponse<any>>;
}

const defaultToolValues: Record<ToolId, Record<string, unknown>> = {
  "firebase-create-user": { name: "", email: "", password: "" },
  "firebase-get-claims": { userId: "" },
  "firebase-update-claims": { userId: "", isAdmin: true },
  "firebase-login": { email: "", password: "" },
  "generate-secret": {},
  "rgb-to-int": { r: 0, g: 0, b: 0 },
  "generate-signing-key": {},
  "generate-application-token": { applicationId: "", customerId: "", tokenAction: "0" },
  "generate-appsettings-app-token": { applicationId: "" },
  "generate-appsettings-appreg-token": { applicationRegistrationId: "" },
  "generate-cache-refresh-token": { applicationId: "" },
  "generate-vehicle-lookup-token": { applicationId: "" },
  "gt-motive-parts-lookup": { estimateId: "", vin: "", cupis: "70010,6101L" },
  "gt-motive-cache-export": {},
  "import-ebay-images": { itemId: "", batchSize: "" },
};

export default function ToolsPage() {
  const { setInfo } = usePageNameContext();
  const { addNotification } = useNotification();
  const mutations = useToolMutations();
  const { customers } = useCustomers();
  const { applications } = useApplications();

  const [activeToolId, setActiveToolId] = useState<ToolId | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [result, setResult] = useState<IDefaultResponse<any> | null>(null);
  const [formValues, setFormValues] = useState<Record<ToolId, Record<string, unknown>>>(defaultToolValues);
  const [copyableValue, setCopyableValue] = useState<string | null>(null);
  const [copyValueModalTitle, setCopyValueModalTitle] = useState("Generated Value");

  useEffect(() => {
    setInfo({
      name: "Tools",
      desc: "Run admin tools safely from one place",
      pageTitle: "Tools",
    });
  }, [setInfo]);

  const customerOptions = useMemo(
    () =>
      (customers.data ?? []).map((customer) => ({
        label: customer.name,
        value: customer.id,
      })),
    [customers.data]
  );

  const applicationOptions = useMemo(
    () =>
      (applications.data ?? []).map((application) => ({
        label: application.name,
        value: application.id,
      })),
    [applications.data]
  );

  const tools = useMemo<ToolDefinition[]>(
    () => [
      {
        id: "firebase-create-user",
        title: "Create Firebase User",
        subtitle: "Create a new Firebase user account",
        warningText: "This will create a real Firebase user. Check email and password details before continuing.",
        hoverClassName: "hover:border-blue-300 hover:bg-blue-50/40",
        icon: UserCircleIcon,
        fields: [
          { name: "name", label: "Name", type: "text", required: true },
          { name: "email", label: "Email", type: "text", required: true },
          { name: "password", label: "Password", type: "password", required: true },
        ],
        execute: (values) => mutations.createFirebaseUser.mutateAsync(values as any),
      },
      {
        id: "firebase-get-claims",
        title: "Get Firebase Claims",
        subtitle: "Read custom claims for a user",
        warningText: "This fetches claim data for the provided user id.",
        hoverClassName: "hover:border-indigo-300 hover:bg-indigo-50/40",
        icon: ShieldCheckIcon,
        fields: [{ name: "userId", label: "User ID", type: "text", required: true }],
        execute: (values) => mutations.getFirebaseClaims.mutateAsync(values as any),
      },
      {
        id: "firebase-update-claims",
        title: "Update Firebase Claims",
        subtitle: "Set isAdmin claim on a user",
        warningText: "This changes a user's admin permissions immediately.",
        hoverClassName: "hover:border-violet-300 hover:bg-violet-50/40",
        icon: ShieldCheckIcon,
        fields: [
          { name: "userId", label: "User ID", type: "text", required: true },
          { name: "isAdmin", label: "Set isAdmin to true", type: "checkbox" },
        ],
        execute: (values) => mutations.updateFirebaseClaims.mutateAsync(values as any),
      },
      {
        id: "firebase-login",
        title: "Firebase Login",
        subtitle: "Generate Firebase ID token",
        warningText: "This logs in with the provided credentials and returns a live token.",
        hoverClassName: "hover:border-sky-300 hover:bg-sky-50/40",
        icon: KeyIcon,
        fields: [
          { name: "email", label: "Email", type: "text", required: true },
          { name: "password", label: "Password", type: "password", required: true },
        ],
        execute: (values) => mutations.firebaseLogin.mutateAsync(values as any),
      },
      {
        id: "generate-secret",
        title: "Generate Secret",
        subtitle: "Generate ApplicationRegistration secret",
        warningText: "This generates a new secret value. Copy it safely after creation.",
        hoverClassName: "hover:border-fuchsia-300 hover:bg-fuchsia-50/40",
        icon: SparklesIcon,
        fields: [],
        execute: () => mutations.generateApplicationRegistrationSecret.mutateAsync(),
      },
      {
        id: "rgb-to-int",
        title: "RGB to Int",
        subtitle: "Convert RGB colour to integer",
        warningText: "This tool converts RGB values into a single integer colour format.",
        hoverClassName: "hover:border-rose-300 hover:bg-rose-50/40",
        icon: PaintBrushIcon,
        fields: [
          { name: "r", label: "R", type: "number", required: true },
          { name: "g", label: "G", type: "number", required: true },
          { name: "b", label: "B", type: "number", required: true },
        ],
        execute: (values) => mutations.convertRgbToInt.mutateAsync(values as any),
      },
      {
        id: "generate-signing-key",
        title: "Generate Signing Key",
        subtitle: "Generate a customer signing key",
        warningText: "This creates a new signing key value. Store and use it carefully.",
        hoverClassName: "hover:border-cyan-300 hover:bg-cyan-50/40",
        icon: KeyIcon,
        fields: [],
        execute: () => mutations.generateCustomerSigningKey.mutateAsync(),
      },
      {
        id: "generate-application-token",
        title: "Application Token",
        subtitle: "Generate application token",
        warningText: "This creates an application token for live integration use.",
        hoverClassName: "hover:border-emerald-300 hover:bg-emerald-50/40",
        icon: BoltIcon,
        fields: [
          {
            name: "applicationId",
            label: "Application",
            type: "select",
            options: applicationOptions,
            required: true,
          },
          {
            name: "customerId",
            label: "Customer",
            type: "select",
            options: customerOptions,
            required: true,
          },
          {
            name: "tokenAction",
            label: "Token Action",
            type: "select",
            options: [
              { label: "None", value: "0" },
              { label: "API Token", value: "1" },
            ],
            required: true,
          },
        ],
        execute: (values) =>
          mutations.generateApplicationToken.mutateAsync({
            applicationId: values.applicationId as string,
            customerId: values.customerId as string,
            tokenAction: Number(values.tokenAction ?? 0),
          } as any),
      },
      {
        id: "generate-appsettings-app-token",
        title: "AppSettings Token (Application)",
        subtitle: "Generate appsettings token for application",
        warningText: "This creates an appsettings token tied to an application id.",
        hoverClassName: "hover:border-teal-300 hover:bg-teal-50/40",
        icon: RectangleStackIcon,
        fields: [
          {
            name: "applicationId",
            label: "Application",
            type: "select",
            options: applicationOptions,
            required: true,
          },
        ],
        execute: (values) => mutations.generateAppSettingsApplicationToken.mutateAsync(values as any),
      },
      {
        id: "generate-appsettings-appreg-token",
        title: "AppSettings Token (App Registration)",
        subtitle: "Generate appsettings token for app registration",
        warningText: "This creates an appsettings token tied to an application registration id.",
        hoverClassName: "hover:border-lime-300 hover:bg-lime-50/40",
        icon: RectangleStackIcon,
        fields: [
          {
            name: "applicationRegistrationId",
            label: "Application Registration ID",
            type: "text",
            required: true,
          },
        ],
        execute: (values) => mutations.generateAppSettingsApplicationRegistrationToken.mutateAsync(values as any),
      },
      {
        id: "generate-cache-refresh-token",
        title: "Cache Refresh Token",
        subtitle: "Generate cache refresh token for application",
        warningText: "This token can trigger cache refresh-related actions.",
        hoverClassName: "hover:border-amber-300 hover:bg-amber-50/40",
        icon: ArrowPathRoundedSquareIcon,
        fields: [
          {
            name: "applicationId",
            label: "Application",
            type: "select",
            options: applicationOptions,
            required: true,
          },
        ],
        execute: (values) => mutations.generateCacheRefreshToken.mutateAsync(values as any),
      },
      {
        id: "generate-vehicle-lookup-token",
        title: "Vehicle Lookup Token",
        subtitle: "Generate vehicle lookup token for application",
        warningText:
          "Long-lived token for Betty's vehicle lookup endpoints (makes, ranges, VRM, options). PartPro Trade uses one for its buying-rule dropdowns.",
        hoverClassName: "hover:border-sky-300 hover:bg-sky-50/40",
        icon: TruckIcon,
        fields: [
          {
            name: "applicationId",
            label: "Application",
            type: "select",
            options: applicationOptions,
            required: true,
          },
        ],
        execute: (values) => mutations.generateVehicleLookupToken.mutateAsync(values as any),
      },
      {
        id: "gt-motive-parts-lookup",
        title: "GT Motive Parts Lookup",
        subtitle: "Run GT Motive estimate create/update",
        warningText: "This calls external GT Motive APIs and may take time.",
        hoverClassName: "hover:border-orange-300 hover:bg-orange-50/40",
        icon: WrenchScrewdriverIcon,
        fields: [
          { name: "estimateId", label: "Estimate ID (optional)", type: "text" },
          { name: "vin", label: "VIN (optional)", type: "text" },
          {
            name: "cupis",
            label: "CUPIS (comma-separated, optional)",
            type: "textarea",
            placeholder: "70010,6101L",
          },
        ],
        execute: (values) => {
          const rawCupis = (values.cupis as string) ?? "";
          const parsedCupis = rawCupis
            .split(/[,\n]/)
            .map((x) => x.trim())
            .filter(Boolean);

          return mutations.runGtMotivePartsLookup.mutateAsync({
            estimateId: (values.estimateId as string) || undefined,
            vin: (values.vin as string) || undefined,
            cupis: parsedCupis.length ? parsedCupis : undefined,
          });
        },
      },
      {
        id: "gt-motive-cache-export",
        title: "GT Motive Cache Export",
        subtitle: "Export cache CSV to server path",
        warningText: "This writes a CSV file to C:\\bin\\GT Motive Export.csv on the API server.",
        hoverClassName: "hover:border-red-300 hover:bg-red-50/40",
        icon: BeakerIcon,
        fields: [],
        execute: () => mutations.exportGtMotiveCache.mutateAsync(),
      },
      {
        id: "import-ebay-images",
        title: "Import eBay Images",
        subtitle: "Import images from scrape data",
        warningText: "This can process and upload many images; run only when intended.",
        hoverClassName: "hover:border-pink-300 hover:bg-pink-50/40",
        icon: ArrowPathRoundedSquareIcon,
        fields: [
          { name: "itemId", label: "Item ID (optional)", type: "number" },
          { name: "batchSize", label: "Batch Size (optional)", type: "number" },
        ],
        execute: (values) =>
          mutations.importEbayImages.mutateAsync({
            itemId: values.itemId ? Number(values.itemId) : undefined,
            batchSize: values.batchSize ? Number(values.batchSize) : undefined,
          }),
      },
    ],
    [applicationOptions, customerOptions, mutations]
  );

  const activeTool = useMemo(
    () => (activeToolId ? tools.find((tool) => tool.id === activeToolId) ?? null : null),
    [activeToolId, tools]
  );

  const activeValues = activeToolId ? formValues[activeToolId] : {};

  const setToolFieldValue = (name: string, value: unknown) => {
    if (!activeToolId) return;

    setFormValues((prev) => ({
      ...prev,
      [activeToolId]: {
        ...prev[activeToolId],
        [name]: value,
      },
    }));
  };

  const validateTool = (tool: ToolDefinition, values: Record<string, unknown>) => {
    const missingField = tool.fields.find((field) => {
      if (!field.required) return false;
      const value = values[field.name];
      return value === null || value === undefined || `${value}`.trim() === "";
    });

    return missingField?.label;
  };

  const runTool = async () => {
    if (!activeTool || !activeToolId) return;

    const missingField = validateTool(activeTool, activeValues as Record<string, unknown>);
    if (missingField) {
      addNotification({
        variant: "error",
        primaryText: `${missingField} is required`,
      });
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await activeTool.execute(activeValues as Record<string, unknown>);
      setResult(response);
      addNotification({
        variant: response.success ? "success" : "error",
        primaryText: response.success ? `${activeTool.title} completed` : `${activeTool.title} failed`,
      });

      if (response.success) {
        const extractedValue = extractCopyableValue(response.data);
        if (extractedValue) {
          setCopyValueModalTitle(activeTool.title);
          setCopyableValue(extractedValue);
        }

        setActiveToolId(null);
      }
    } catch (error: any) {
      addNotification({
        variant: "error",
        primaryText: error?.message || "Tool execution failed",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
        {tools.map((tool) => (
          <ToolTile
            key={tool.id}
            title={tool.title}
            subtitle={tool.subtitle}
            hoverClassName={tool.hoverClassName}
            icon={tool.icon}
            onClick={() => setActiveToolId(tool.id)}
          />
        ))}
      </div>

      {result && (
        <div className="rounded-xl border border-gray-200 bg-white p-4">
          <h3 className="text-sm font-semibold text-gray-700">Last Response</h3>
          <pre className="mt-2 overflow-auto text-xs text-gray-700 bg-gray-50 rounded p-3">
            {JSON.stringify(result, null, 2)}
          </pre>
        </div>
      )}

      {activeTool && activeToolId && (
        <ToolActionModal
          open={Boolean(activeTool)}
          setOpen={(open) => {
            if (!open) {
              setActiveToolId(null);
            }
          }}
          title={activeTool.title}
          description={activeTool.subtitle}
          warningText={activeTool.warningText}
          fields={activeTool.fields}
          values={activeValues}
          onChange={setToolFieldValue}
          onSubmit={runTool}
          isSubmitting={isSubmitting}
        />
      )}

      {copyableValue !== null && (
        <ToolValueResultModal
          open={copyableValue !== null}
          setOpen={(open) => {
            if (!open) {
              setCopyableValue(null);
            }
          }}
          title={copyValueModalTitle}
          value={copyableValue}
          onCopy={async () => {
            try {
              await navigator.clipboard.writeText(copyableValue);
              addNotification({
                variant: "success",
                primaryText: "Copied to clipboard",
              });
            } catch {
              addNotification({
                variant: "error",
                primaryText: "Failed to copy value",
              });
            }
          }}
        />
      )}
    </div>
  );
}

function extractCopyableValue(data: unknown): string | null {
  if (data === null || data === undefined) return null;

  if (typeof data === "string" || typeof data === "number" || typeof data === "boolean") {
    return `${data}`;
  }

  if (typeof data === "object") {
    const valueObject = data as Record<string, unknown>;

    const preferredKeys = ["value", "idToken", "filePath"];
    for (const key of preferredKeys) {
      const candidate = valueObject[key];
      if (typeof candidate === "string" || typeof candidate === "number" || typeof candidate === "boolean") {
        return `${candidate}`;
      }
    }
  }

  return null;
}
